import { getAgentConfigurationFromCdnByAgentId } from "@nemuru-eng/js-api-client/slim";
import React, { useEffect, useState } from "react";
import { useTranslation } from "../../../../../utils/hooks/useTranslation";
import { LenderName } from "@nemuru-eng/js-api-client";

const CONSUMER_LOAN_LENDERS = [
  LenderName.CAIXABANK,
  LenderName.BBVA,
  LenderName.BS,
  LenderName.CETELEM,
  LenderName.SOYOU,
  LenderName.ABANCA,
  LenderName.SANTANDER_CONSUMER,
];

const GDPR_CONTACT_INFORMATION_BY_LENDER_NAME = {
  [LenderName.CAIXABANK]: (
    <div>
      <strong>CaixaBank Payments & Consumer, E.F.C., E.P., S.A.</strong>,
      sociedad unipersonal (CaixaBank Payments & Consumer), con domicilio en
      Avenida de Manoteras nº 20. Edificio París. 28050 Madrid (28050) y NIF
      A-08980153. Puedes ejercer tus derechos en{" "}
      <a
        href="www.caixabankpc.com/ejerciciodederechos"
        target="_blank"
        rel="noopener noreferrer"
      >
        www.caixabankpc.com/ejerciciodederechos
      </a>
      . Contacto del delegado de protección de datos:{" "}
      <a
        href="www.caixabank.es/particular/general/dpo_es.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        www.caixabank.es/particular/general/dpo_es.html
      </a>
      . Acceda a información adicional en el siguiente enlace:{" "}
      <a
        href="www.caixabankpc.com/es/politica-de-privacidad"
        target="_blank"
        rel="noopener noreferrer"
      >
        www.caixabankpc.com/es/politica-de-privacidad
      </a>
    </div>
  ),
  [LenderName.CETELEM]: (
    <div>
      <strong>Banco Cetelem, S.A.U. (Cetelem)</strong>, con domicilio social en
      Paseo de los Melancólicos, 14A 28005 Madrid; contactos de atención al
      cliente{" "}
      <a href="mailto:SACquejasyreclamaciones@cetelem.es">
        SACquejasyreclamaciones@cetelem.es
      </a>
      , protección de datos y DPO{" "}
      <a href="mailto:DPO@cetelem.es">DPO@cetelem.es</a>. Acceda a información
      adicional en el siguiente enlace:{" "}
      <a
        href="https://www.cetelem.es/proteccion-de-datos"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.cetelem.es/proteccion-de-datos
      </a>
    </div>
  ),
};

const LENDERS_WITH_GDPR_CONTACT_INFORMATION = Object.keys(
  GDPR_CONTACT_INFORMATION_BY_LENDER_NAME
);

export const TechnologicalPlatformTerms = ({ agentId }) => {
  const { getTextIdTranslation } = useTranslation();
  const [state, setState] = useState({
    lenderNames: null,
    businessName: null,
  });

  useEffect(() => {
    getAgentConfigurationFromCdnByAgentId({ agentId }).then((response) => {
      const lenderNames = response.agentsInLenderContext
        .filter(
          (lender) =>
            lender.product === "consumer_loan" &&
            CONSUMER_LOAN_LENDERS.includes(lender.lenderName)
        )
        .map((lender) => lender.lenderName);

      const businessName = response.merchant.businessName;
      setState({ lenderNames: [...new Set(lenderNames)], businessName });
    });
  }, []);

  const lenderLegalEntityNames = {
    [LenderName.CAIXABANK]: "CaixaBank Payments & Consumer E.F.C, E.P. S.A.",
    [LenderName.CETELEM]: "Banco Cetelem, S.A.U. (Cetelem)",
  };

  const { lenderNames, businessName } = state;
  if (!lenderNames || !businessName) return null;

  const formattedLenderNames = lenderNames
    .map(
      (lender) => lenderLegalEntityNames[lender] || getTextIdTranslation(lender)
    )
    .join(", ");

  const needsLenderContactInformation =
    LENDERS_WITH_GDPR_CONTACT_INFORMATION.some((lender) =>
      lenderNames.includes(lender)
    );

  return (
    <>
      <h3>Información sobre el tratamiento de sus datos</h3>
      <p>
        En cumplimiento del deber de informar del Reglamento (UE) 2016/679 del
        Parlamento Europeo y del Consejo, de 27 de abril de 2016, relativo a la
        protección de las personas físicas en lo que respecta al tratamiento de
        datos personales y a la libre circulación de estos datos (“RGPD”) y de
        la Ley Orgánica 3/2018, de 5 de diciembre, de protección de datos
        personales y garantía de los derechos digitales (“LOPDGDD”), a
        continuación, se proporciona al usuario información sobre el tratamiento
        de sus datos personales llevados a cabo en la Plataforma web de gestión
        de financiación Nemuru (en adelante la “Plataforma”).
      </p>
      <p>
        La presente política de privacidad se refiere exclusivamente al
        tratamiento de datos personales efectuado desde la Plataforma en el
        marco del proceso de solicitud, análisis y gestión de operaciones de
        financiación ofrecidas por entidades financieras colaboradoras.
      </p>
      <h3>1.- Responsable de los datos</h3>
      <p>
        Los responsables del tratamiento de los datos personales son las
        entidades financieras colaboradoras {formattedLenderNames} (en adelante,
        los “Responsables”) que ofrecen los servicios de financiación a través
        de la Plataforma.
      </p>
      <p>
        El {businessName} (en adelante, el “Comercio”) actúa únicamente como
        encargado del tratamiento en nombre y por cuenta de dichas entidades
        financieras, limitándose a recabar, transmitir y custodiar los datos
        necesarios para la evaluación y gestión de las solicitudes de
        financiación.
      </p>
      <p>
        Nemuru Digital, S.L. actúa como subencargado del tratamiento de las
        Entidades Financieras colaboradoras, prestando servicios tecnológicos de
        gestión de la Plataforma bajo las instrucciones del Prescriptor y, en
        última instancia, de la entidad financiera responsable, conforme al
        artículo 28 RGPD.
      </p>
      <p>
        La lista actualizada de entidades financieras colaboradoras y enlaces a
        sus políticas de privacidad estará disponible en el apartado “Entidades
        financieras habilitadas” de la Plataforma, para garantizar la
        transparencia exigida por el artículo 13 RGPD.
      </p>
      <h3>2.- Finalidades del tratamiento y legitimación</h3>
      <p>
        Los datos personales facilitados por el usuario en la Plataforma serán
        tratados por las entidades financieras colaboradoras, a través del
        Comercio en su condición de encargado, para las siguientes finalidades:
      </p>
      <ul>
        <li>
          Evaluar su solvencia económica y patrimonial, mediante la lectura y
          análisis de su información bancaria y financiera, así como la
          verificación de la existencia de una relación previa con el
          solicitante y, en su caso, de la información asociada a la misma, con
          la finalidad de gestionar, tramitar y evaluar la solicitud de
          financiación y las condiciones aplicables. Base legitimadora: la
          ejecución de medidas precontractuales solicitadas por el interesado y
          la ejecución de un contrato, de conformidad con el artículo 6.1.b) del
          RGPD), así como el cumplimiento de obligaciones legales establecidas
          en la Normativa Prudencial y de Solvencia y la Normativa de Préstamo
          Responsable, en virtud del artículo 6.1.c) del RGPD.
        </li>
        <li>
          Gestionar la relación contractual derivada de la solicitud y, en su
          caso, concesión de financiación. Base legitimadora: la ejecución de un
          contrato en el que el interesado es parte, conforme al artículo 6.1.b)
          del RGPD.
        </li>
        <li>
          Cumplir con obligaciones legales aplicables a los responsables, en
          especial las relativas a la prevención del blanqueo de capitales, las
          políticas de sanciones y contramedidas financieras internacionales, la
          Normativa de Préstamo Responsable, la verificación de identidad y la
          prevención del fraude. Base legitimadora: el cumplimiento de
          obligaciones legales, en virtud del artículo 6.1.c) del RGPD y el
          interés legítimo de los responsables, según lo dispuesto en el
          artículo 6.1.f) del RGPD.
        </li>
        <li>
          Elaborar perfiles crediticios y tomar decisiones automatizadas en
          relación con la concesión o denegación del crédito solicitado. Base
          legitimadora: la ejecución de medidas precontractuales y el interés
          legítimo de los responsables en la gestión del riesgo crediticio. En
          este sentido, el interesado tendrá derecho a obtener intervención
          humana en los tratamientos, a expresar su punto de vista, a obtener
          una explicación sobre la decisión tomada en base a dicho tratamiento y
          a impugnar dicha decisión.
        </li>
        <li>
          Remitir comunicaciones comerciales sobre productos o servicios
          financieros similares, siempre que el usuario haya prestado su
          consentimiento o exista interés legítimo conforme a la normativa
          aplicable. Base legitimadora: el consentimiento del interesado, en
          virtud del artículo 6.1.a) o el interés legítimo, previsto en el
          artículo 6.1.f) según proceda.
        </li>
        <li>
          Verificación de identidad mediante video-identificación, que implica
          la captura y tratamiento de su imagen, voz y documento identificativo,
          con el fin de confirmar su identidad y prevenir el fraude, en
          cumplimiento de la normativa aplicable sobre prevención del blanqueo
          de capitales y financiación del terrorismo (Ley 10/2010)
        </li>
      </ul>
      <h3>3.- Categoría de datos personales</h3>
      <p>
        Los responsables podrán tratar los siguientes datos que se han recogido
        directamente del interesado e información que obtengan de las fuentes
        más adelante indicadas:
      </p>
      <p>
        Datos identificativos y de contacto tales como: nombre, apellidos, DNI,
        pasaporte o NIE, nacionalidad, direcciones postales, direcciones
        electrónicas, teléfono fijo, teléfono móvil.
      </p>
      <p>
        Datos económicos y de solvencia patrimonial tales como: ingresos y
        gastos.
      </p>
      <p>
        Datos sociodemográficos tales como: edad, estado civil, situación
        familiar, residencias, estudios y ocupación (tipo de contrato,
        antigüedad en el empleo) o pertenencia a colectivos.
      </p>
      <p>
        En cumplimiento de la legislación en materia de prevención de blanqueo
        de capitales, la Ley 2/2011, de 4 de marzo, de Economía Sostenible y la
        Ley 10/2014, de ordenación, supervisión y solvencia de entidades de
        crédito, las entidades de crédito deben consultar la información
        relativa a la actividad económica de los solicitantes y comprobar su
        solvencia.
      </p>
      <p>
        Para ello, y con la finalidad de realizar el análisis de solvencia y
        estudio de la financiación solicitada, los responsables podrán solicitar
        información relativa a los antecedentes crediticios a la Central de
        Información de Riesgos de Banco de España (CIR), a sistemas de
        información crediticia (Servicios de Información del Crédito S.L de
        ASNEF/Equifax, Experian Bureau de Crédito), y a ficheros comunes de
        solvencia patrimonial y crédito.
      </p>
      <p>
        La base legal de estas consultas es el interés legítimo de la Entidad
        financiera en cumplir con las obligaciones de crédito responsable y en
        prevención del fraude.
      </p>
      <h3>4.- Plazos y criterios de conservación de los datos</h3>
      <p>
        Los datos personales se conservarán durante el tiempo necesario para la
        tramitación y gestión de la solicitud de financiación y, posteriormente,
        durante los plazos legalmente exigidos para el cumplimiento de
        obligaciones normativas o la defensa de reclamaciones.
      </p>
      <p>
        El Comercio conservará los datos únicamente durante el tiempo necesario
        para prestar el servicio y los eliminará o anonimizará una vez
        finalizada su función como encargado del tratamiento.
      </p>
      <h3>5.- Seguridad en el tratamiento de datos personales</h3>
      <p>
        El Comercio y los responsables colaboradoras aplican medidas técnicas y
        organizativas adecuadas, incluyendo cifrado y protocolos de comunicación
        segura, con el objetivo de garantizar la confidencialidad, integridad,
        disponibilidad y autenticidad del emisor.
      </p>
      <h3>6.- Destinatarios de cesiones</h3>
      <p>
        Los datos personales podrán ser comunicados a los siguientes
        destinatarios:
      </p>
      <ul>
        <li>
          Entidades financieras colaboradoras, responsables del tratamiento,
          para la evaluación de solvencia, análisis crediticio y gestión de la
          financiación.
        </li>
        <li>
          Autoridades públicas y organismos competentes, cuando exista
          obligación legal o requerimiento judicial o administrativo.
        </li>
        <li>
          Proveedores de servicios del Comercio, que actuarán como subencargados
          del tratamiento bajo contrato conforme al artículo 28 RGPD.
        </li>
      </ul>
      <p>
        No se prevén otras cesiones ni transferencias internacionales de datos
        fuera del Espacio Económico Europeo.
      </p>
      <h3>7.- Derechos de las personas interesadas</h3>
      <p>
        El usuario puede ejercer ante las entidades financieras responsables los
        derechos de acceso, rectificación, supresión, limitación del
        tratamiento, oposición, portabilidad y a no ser objeto de decisiones
        automatizadas, incluyendo la elaboración de perfiles.
      </p>
      <p>
        Asimismo, podrá retirar el consentimiento prestado en cualquier momento,
        sin que ello afecte a la licitud del tratamiento basado en el
        consentimiento previo.
      </p>
      <p>
        Para ejercer estos derechos, el interesado podrá dirigirse directamente
        a la entidad financiera responsable, cuyos datos de contacto se
        encuentran disponibles en el apartado de “Entidades financieras
        habilitadas”.
      </p>
      <p>
        En todo caso, el interesado tiene derecho a presentar una reclamación
        ante la Agencia Española de Protección de Datos (
        <a
          href="https://www.aepd.es/"
          target="_blank"
          rel="noopener noreferrer"
        >
          www.aepd.es
        </a>
        ) si considera que el tratamiento de sus datos no se ajusta a la
        normativa vigente.
      </p>
      {needsLenderContactInformation && (
        <div style={{ marginTop: "20px" }}>
          <h4>
            <strong>7.1- Entidades financieras habilitadas:</strong>
          </h4>
          <ul>
            {lenderNames.map((lender) => {
              const GDPR_CONTACT_INFORMATION =
                GDPR_CONTACT_INFORMATION_BY_LENDER_NAME[lender];
              if (!GDPR_CONTACT_INFORMATION) return null;
              return <li key={lender}>{GDPR_CONTACT_INFORMATION}</li>;
            })}
          </ul>
        </div>
      )}
    </>
  );
};
